<script>
  import '$lib/../theme/main.scss';
  import { page } from '$app/stores';
  import { invalidateAll } from '$app/navigation';
  import {
    open,
    mobile,
    channel,
    currentChannel,
    clientSideValidationDisabled
  } from '$lib/store';
  import { audio, states } from '$lib/audio-store';
import { playlist } from '$lib/playlist-store';
import Snackbar, { Actions, Label } from '@smui/snackbar';
import Button, { Icon } from '@smui/button';
import IconButton from '@smui/icon-button';
import { Stretch } from 'svelte-loading-spinners';
import Schedule from '$lib/client/shared/schedule.svelte';
import AudioPlayer from '$lib/client/shared/audio-player.svelte';
import logo from '$assets/most_rated_star.png';
import TopAppBar, { Row, Section, Title } from '@smui/top-app-bar';
import Burger from '$lib/client/shared/burger.svelte';
import ChannelSelector from '$lib/client/shared/channel-selector.svelte';
import Portal from '$lib/client/shared/portal.svelte';
import { MetaTags } from 'svelte-meta-tags';

let snackBar;
let innerWidth;

export let data;
const { channels } = data;

$: {
    if (snackBar && $page.data.flash) snackBar.open();
}

  $: {
    mobile.set(innerWidth < 480);
  }

  if (data.clientSideValidationDisabled)
    $clientSideValidationDisabled = data.clientSideValidationDisabled;

  function playCurrentChannel() {
    audio.changeChannel($currentChannel)
    playlist.init();
    setTimeout(audio.play, 1000);
  }
</script>

<svelte:window bind:innerWidth />

<MetaTags
  title={$playlist.currentTrack
    ? `${$playlist.currentTrack.title} - ${$playlist.currentTrack.artist.name}`
    : $page.data.seo.title}
  titleTemplate="%s | MOST RATED"
  description={$page.data.seo.description}
  canonical={$page.data.seo.canonical}
  openGraph={{
    type: $page.data.seo.type || 'website',
    url: $page.data.seo.canonical,
    title: $page.data.seo.og?.title || $page.data.seo.title,
    description: $page.data.seo.description,
    images: $page.data.seo.image?.url
      ? [$page.data.seo.image]
      : [
          {
            url: $playlist.currentTrack?.images
              ? $playlist.currentTrack?.images?.card.replace('100x100', '1000x1000')
              : logo,
            width: 1000,
            height: 1000,
            alt: `Now playing ${$playlist.currentTrack?.title} by ${$playlist.currentTrack?.artist?.name}`
          }
        ],
    siteName: 'MOST RATED'
  }}
  twitter={{
    handle: '@mostrated',
    site: '@mostrated',
    cardType: 'summary',
    title: $page.data.seo.og?.title || $page.data.seo.title,
    description: $page.data.seo.description
  }}
  facebook={{ appId: import.meta.env.VITE_FACEBOOK_APP_ID }}
  additionalMetaTags={$page.data.seo.extras}
/>

<Portal />

<TopAppBar color="secondary" id="navigation" variant="static" dense={true}>
  <Row>
    <Section>
      <a id="logo" class="none" href="/"><span class="u-mr-small">MOST</span><span>RATED</span></a>
    </Section>

    <Section align="end" toolbar>
      <div class="controls">
        <div class="listen-now u-mr-small">
          {#if $audio.state === states.PLAYING}
            <h2 class="stop lined-up t-uppercase u-mt-none u-mb-none" on:click={audio.stop}>
              <Icon class="material-icons t-primary u-ml-small">stop_circle</Icon>
            </h2>
          {/if}

          {#if $audio.state === states.STOPPED || $audio.state === states.OFFLINE}
            <h2 class="play lined-up t-uppercase u-mt-none u-mb-none" on:click={playCurrentChannel}>
              <span class="desktop-only">Listen Now</span>
              <Icon class="material-icons t-primary u-ml-small">play_circle</Icon>
            </h2>
          {/if}

          {#if $audio.state === states.BUFFERING}
            <Stretch duration="2s" size="40" color="#00CCFE" />
          {/if}
        </div>

        <ChannelSelector {channels} />
        {#if $page.data.session}
          <Burger />
        {:else}
          <div class="u-ml-small u-mr-small">
            <Button class="u-p-small" tag="a" href="/sign-in" color="primary" variant="raised"
              >Sign In</Button
            >
          </div>
        {/if}
      </div>
    </Section>
  </Row>
</TopAppBar>

{#if $page.data.flash}
  <Snackbar id="flash-notice" bind:this={snackBar}>
    <Label>{$page.data.flash}</Label>
    <Actions>
      <IconButton class="material-icons" title="Dismiss">close</IconButton>
    </Actions>
  </Snackbar>
{/if}

<main>
  <slot />
</main>

<aside id="bottom-bar">
  <Schedule />

  <AudioPlayer />
</aside>

<footer class="main-footer b--solid-t-small b--secondary t-center {$open ? 'schedule-open' : ''}">
  <div>
    <a href="/">Home</a>
    <a href="/{['music', $currentChannel?.slug, 'schedules'].filter((x) => x).join('/')}"
      >{$currentChannel?.name || ''} Schedules</a
    >
    <a href="/about">About Us</a>
    <a href="/faqs">FAQs</a>
    <a href="/whitepaper.pdf">Whitepaper</a>
    <a href="mailto:mostrated@dangarland.co.uk">Contact Us</a>
    <a href="/cookie-policy">Cookie Policy</a>
    <a href="/privacy-policy">Privacy Policy</a>
    <a href="/license-agreement">License Agreement</a>
  </div>

  <div class="countries u-mt">
    {#each channels as channel}
      <a href={`/music/${channel.slug || ''}`}>{channel.name}</a>
    {/each}
  </div>

  <div class="u-mt-large">
    <span class="social-icon instagram u-mr"
      ><a
        title="Follow us on Instagram"
        href="https://instagram.com/mostratedmusic"
        target="_blank"
        rel="noopener">Instagram</a
      ></span
    >
    <span class="social-icon threads u-mr u-ml"
      ><a
        title="Follow us on Threads"
        href="https://www.threads.net/@mostratedmusic"
        target="_blank"
        rel="noopener">Threads</a
      ></span
    >

    <span class="social-icon x u-mr u-ml">
      <a title="Follow us on X" href="https://x.com/mostrated" target="_blank" rel="noopener">X</a>
    </span>

    <span class="social-icon bluesky u-mr u-ml">
      <a
        title="Follow us on Bluesky"
        href="https://bsky.app/profile/mostrated.bsky.social"
        target="_blank"
        rel="noopener">Bluesky</a
      >
    </span>
  </div>

  <div class="love-letter u-mt-large">Made with ❤️ by Dan for Maria</div>
</footer>
